import { type PostHog } from 'posthog-js';
import { AuthLocalStorageKeys } from '@store/auth';

const usePostHog = defineStore('posthog', () => {
    const postHog = ref<PostHog>();
    const { isWebApp } = useDevice();
    const featureFlags = reactive<Record<string, string | boolean>>({});
    const loadPromise = ref<Promise<unknown>>();

    const utmSource = computed(
        () => postHog.value?.persistence?.props?.utm_source
    );
    const loadPostHog = async () => {
        if (postHog.value) {
            return;
        }
        const { resolve, reject, promise } = Promise.withResolvers();
        loadPromise.value = promise;
        const abortTimer = setTimeout(() => {
            reject();
        }, 5 * 1000);

        const { posthog } = await import('posthog-js');
        posthog.init('phc_6I9FuXTI7Hb789HOwDkGyeWf2TS4ExjOpc6PTZocxFm', {
            api_host: 'https://ph.mrbilit.ir',
            capture_pageview: false,
            autocapture: false,
            loaded: ph => {
                postHog.value = ph;
                const credentials = JSON.parse(
                    localStorage.getItem(AuthLocalStorageKeys.credentials) ||
                        '[]'
                );
                const index = +(
                    localStorage.getItem(
                        AuthLocalStorageKeys.selectedCredentialIndex
                    ) || '0'
                );
                ph.identify(
                    localStorage.loginUuid ||
                        credentials[index]?.uuid ||
                        localStorage.localUuid
                );
                ph.people.set({ environment: useRunEnv() });
                ph.onFeatureFlags((_val, flags) => {
                    if (flags) {
                        clearTimeout(abortTimer);
                        Object.assign(featureFlags, flags);
                    }
                    resolve(true);
                });
                ph.reloadFeatureFlags();
                useRouter().afterEach((to: any) => {
                    nextTick(() => {
                        /* Note:
                         * this might also be a good place to call posthog.register(...) in order to update your properties
                         * on each page view
                         */
                        trackPostHog('$pageview', {
                            $current_url: to.fullPath,
                            isWebApp: isWebApp.value,
                        });
                    });
                });
            }, // If you can already identify your user
        });

        return promise;
    };

    const trackPostHog = async (name: string, attrs?: Record<string, any>) => {
        await loadPromise.value;
        nextTick(() => {
            if (postHog.value) {
                postHog.value.capture(name, {
                    ...attrs,
                    isWebApp: isWebApp.value,
                });
            } else {
                throw new Error(`initial the postHog`);
            }
        });
    };

    return {
        loadPostHog,
        trackPostHog,
        loadPromise,
        featureFlags,
        postHog,
        utmSource,
    };
});

export type PosthogHandler = ReturnType<typeof usePostHog>;

export { usePostHog };
